import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftRight } from "lucide-react";

/**
 * Floating toggle between the current design and the Clearline redesign, holding
 * your place — list stays on the list, and a case stays on the same case.
 *
 * Exists only to make the side-by-side comparison usable; otherwise you have to
 * hand-edit the URL. Deliberately small and corner-pinned so it does not
 * intrude on either layout being judged.
 *
 * Derives the counterpart from the current path rather than taking props, so
 * both designs mount it identically with no wiring.
 *
 * Remove this, src/redesign/, and the /redesign/* routes to drop the comparison.
 */

const REDESIGN_PREFIX = "/redesign";

/**
 * @param {{variant?: "rail"|"icon"}} props
 *   rail  the redesign's 208px text sidebar
 *   icon  the current design's 96px icon rail
 *
 * Rendered inside each sidebar rather than floating over the page: pinned to a
 * corner it sat on top of whichever layout was being judged, which is the one
 * thing a side-by-side comparison should not do.
 */
export default function DesignSwitcher({ variant = "rail" }) {
  const { pathname, search } = useLocation();

  const onRedesign = pathname.startsWith(`${REDESIGN_PREFIX}/`);

  // /cases/ABC <-> /redesign/cases/ABC, /cases <-> /redesign/cases
  const target = onRedesign
    ? pathname.slice(REDESIGN_PREFIX.length)
    : `${REDESIGN_PREFIX}${pathname}`;

  // Only meaningful where both designs exist.
  if (!/^\/(redesign\/)?(cases|property-cases)(\/|$)/.test(pathname)) return null;

  const label = onRedesign ? "Current design" : "New design";
  const title = onRedesign
    ? "Switch to the current design"
    : "Switch to the Clearline redesign";
  const to = `${target}${search}`;

  // Matches the icon rail's own items: 48px icon box, small caps caption.
  if (variant === "icon") {
    return (
      <Link
        to={to}
        title={title}
        className="flex w-full flex-col items-center gap-1.5 rounded-2xl py-2"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl">
          <ArrowLeftRight className="h-8 w-8 text-text-secondary" />
        </div>
        <span className="text-center text-xs font-bold uppercase leading-none tracking-tight text-text-secondary">
          {onRedesign ? "Current" : "New UI"}
        </span>
      </Link>
    );
  }

  // Matches the text rail's nav rows.
  return (
    <Link
      to={to}
      title={title}
      className="flex items-center gap-[7px] rounded-tile px-[10px] py-2 text-[13px] font-medium text-cl-secondary transition-colors hover:bg-cl-tint/60"
    >
      <ArrowLeftRight className="h-[13px] w-[13px] flex-none text-cl-faint" />
      {label}
    </Link>
  );
}
